import React, { useState, useEffect } from "react";
import {
  Home,
  Users,
  Building,
  Settings,
  Menu,
  X,
  Loader2,
  UserIcon,
  FileText,
  Smartphone,
  ClipboardList,
  AlertCircle,
  CreditCard,
  CalculatorIcon,
  Monitor,
  Mail,
  ChevronDown,
  ChevronUp,
  Pen
} from "lucide-react";

// Import your separate components
import UserList from "./components/UserList";
import ClientDetails from "./components/ClientDetails";
import ConnectionDetails from "./components/ConnectionDetails";
import MobilePhones from "./components/MobilePhones";
import NotesComponent from "./components/NotesComponent";
import UserForm from "./components/UserForm";
import ExistingComponent from "./components/ExistingComponent";
import Mechanics from "./components/Mechanics";
import TeamAuth from "./components/Auth";
import TeamList from "./components/TeamList";
import Requests from "./components/Requests";
import TeamService from "./services/teamService";
import NoteListComponent from './components/NoteListComponent';
import NewQuoteForm from './components/NewQuoteForm';
import SignUp from "./components/SignUp"; 
import AdminSystemConfig from "./components/AdminSystemConfig";
import SettingsPage from "./components/Settings";
import dashboardService from "./services/dashboardService";
import emailEditor from "./components/EmailEditor";
import TermsUpload from "./components/TermsUpload";
import ResetPasswordPage from "./components/ResetPasswordPage";
import ContractView from "./components/ContractViewComponent";
import DigitalQuotes from "./components/Digitalquotescoordinator";
import SignaturesManager from "./components/SignaturesManager";
const App = () => {
  // Navigation and routing state
  const [currentRoute, setCurrentRoute] = useState("dashboard");
  const [currentView, setCurrentView] = useState("list");
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [expandedMenu, setExpandedMenu] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (TeamService.isAuthenticated()) {
          const response = await TeamService.getMe();
          setCurrentUser(response.data);
         
          setIsAuthenticated(true);
        }
      } catch (error) {
        TeamService.removeAuthToken();
        TeamService.removeAuthUser();
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);
  
  const [stats, setStats] = useState({
    data: {
      completed_rentals: 0,
      completed_this_month: 0,
      pending_approvals: 0,
    },
  });


  useEffect(() => {
    if (isAuthenticated) {
      dashboardService
        .getDashboardStats()
        .then((data) => setStats(data))
        .catch((err) => console.error("Error loading dashboard stats:", err));
    }
  }, [isAuthenticated]);

  // URL handling - COMPLETELY UPDATED
  const handleRoute = () => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    
    console.log("Current path:", path);
    console.log("Token in URL:", token);

    // Handle reset-password route
    if (path === "/reset-password") {
      console.log("Setting route to reset-password");
      setCurrentRoute("reset-password");
      return;
    }

    // Handle signup route
    if (path === "/signup") {
      setCurrentRoute("signup");
      return;
    }

    // Handle notes route
    const notesMatch = path.match(/^\/notes\/(\d+)/);
    if (notesMatch) {
      const connectionId = parseInt(notesMatch[1], 10);
      setCurrentRoute("notes");
      setSelectedItem(connectionId);
      return;
    }

    // Handle admin-config route
    if (path === "/admin-config") {
      const connectionId = searchParams.get("connectionId");
      setCurrentRoute("admin-config");
      if (connectionId) {
        setSelectedItem(parseInt(connectionId, 10));
      }
      return;
    }

    // Handle other routes
    const route = path.replace("/", "") || "dashboard";
    const knownRoutes = [
      "dashboard",
      "new-quote",
      "clients",
      "connection-details",
      "team",
      "mechanics",
      "admin-config",
      "settings",
      "reset-password",
      "signup"
    ];

    if (knownRoutes.includes(route)) {
      setCurrentRoute(route);
    } else {
      // Default to dashboard for unknown routes
      console.warn("Unknown route, redirecting to dashboard:", route);
      window.history.replaceState({}, "", "/dashboard");
      setCurrentRoute("dashboard");
    }
  };

  useEffect(() => {
    // Add event listener for URL changes
    const handlePopState = () => {
      handleRoute();
    };

    // Initial route check
    handleRoute();

    // Listen for URL changes
    window.addEventListener("popstate", handlePopState);
    
    // Also check URL periodically to catch direct navigation
    const interval = setInterval(() => {
      const currentPath = window.location.pathname;
      if (currentPath === "/reset-password" && currentRoute !== "reset-password") {
        handleRoute();
      }
    }, 100);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      clearInterval(interval);
    };
  }, []);

  const updateURL = (route) => {
    window.history.pushState({}, "", `/${route}`);
    setCurrentRoute(route);
    setCurrentView("list");
    setSelectedItem(null);
  };

  // Add logout handler
  const handleLogout = async () => {
    try {
      await TeamService.logout();
      setIsAuthenticated(false);
      setCurrentUser(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      setIsAuthenticated(false);
      setCurrentUser(null);
      window.location.href = "/";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 text-lg">Loading application...</p>
        </div>
      </div>
    );
  }

  // ✅ Show reset password page (accessible without authentication)
  if (currentRoute === "reset-password") {
    console.log("Rendering ResetPasswordPage");
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    
    return (
      <ResetPasswordPage />
    );
  }

  // Show signup page for ALL users on /signup route, regardless of authentication
  if (currentRoute === "signup") {
    const searchParams = new URLSearchParams(window.location.search);
    const inviteToken = searchParams.get('invite');
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    
    console.log('Rendering SignUp component with:', { inviteToken, token, email });
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <SignUp 
            inviteToken={inviteToken} 
            onSignupSuccess={() => {
              // After successful signup, redirect to dashboard
              window.location.href = "/dashboard";
            }}
            onNavigateToLogin={() => {
              window.location.href = "/signup";
            }}
            onNavigateToDashboard={() => {
              window.location.href = "/dashboard";
            }}
            isAuthenticated={isAuthenticated}
            currentUser={currentUser}
          />
        </div>
      </div>
    );
  }

  // Show auth for unauthenticated users on other routes
  if (!isAuthenticated) {
    return (
      <TeamAuth
        onLoginSuccess={async () => {
          try {
            const response = await TeamService.getMe();
            setCurrentUser(response.data);
            setIsAuthenticated(true);
            setCurrentRoute("dashboard");
            window.history.pushState({}, "", "/dashboard");
          } catch (error) {
            console.error("Error getting user info:", error);
            setIsAuthenticated(true);
          }
        }}
      />
    );
  }

  // Rest of your code for authenticated users (dashboard, etc.)
  // Navigation configuration
const getNavigationItems = () => {
  // Support role gets specific access
  if (currentUser?.role === "Support") {
    return [
      { 
        id: "dashboard", 
        label: "Dashboard", 
        icon: Home, 
        component: null 
      },
      
     
      {
        id: "contracts",
        label: "Quotes",
        icon: AlertCircle,
        component: ContractView,
      },
      { 
        id: "settings", 
        label: "Settings", 
        icon: Settings, 
        component: SettingsPage 
      },
    ];
  }

  // Sales role gets specific access
  if (currentUser?.role === "Sales") {
    return [
      { 
        id: "dashboard", 
        label: "Dashboard", 
        icon: Home, 
        component: null 
      },
      { 
        id: "new-quote", 
        label: "Quotes", 
        icon: FileText, 
        component: NewQuoteForm 
      },
      { 
        id: "settings", 
        label: "Settings", 
        icon: Settings, 
        component: SettingsPage 
      },
    ];
  }

  // For other non-admin roles (Team Member, Manager, etc.)
  if (currentUser?.role !== "Admin") {
    return [
      { 
        id: "dashboard", 
        label: "Dashboard", 
        icon: Home, 
        component: null 
      },
      { 
        id: "new-quote", 
        label: "Quotes", 
        icon: FileText, 
        component: NewQuoteForm 
      },
      { 
        id: "settings", 
        label: "Settings", 
        icon: Settings, 
        component: SettingsPage 
      },
    ];
  }

  // Admin gets full access
  return [
    { 
      id: "dashboard", 
      label: "Dashboard", 
      icon: Home, 
      component: null 
    },
    {
      id: "team",
      label: "Team Management",
      icon: UserIcon,
      component: TeamList,
    },
    {
      id: "connection-details",
      label: "Customer Details",
      icon: Users,
      component: ConnectionDetails,
    },
    { 
      id: "new-quote", 
      label: "Quotes", 
      icon: FileText, 
      component: NewQuoteForm 
    },
    {
      id: "clients",
      label: "Notes",
      icon: Building,
      component: NoteListComponent,
    },
    {
      id: "upload",
      label: "Terms & Conditions",
      icon: ClipboardList,
      component: TermsUpload,
    },
    {
      id: "template",
      label: "Email Template",
      icon: Mail,
      component: emailEditor,
    },
    {
      id: "mechanics",
      label: "Mechanics",
      icon: Monitor,
      component: Mechanics,
    },
    {
      id: "contracts",
      label: "Contracts",
      icon: AlertCircle,
      component: ContractView,
    },
    {
     id: "signatures",
      label: "Signatures",
      icon: Pen,
      component: SignaturesManager,
    },
    //{
    //  id: "digitalservices",
    //  label: "Digital Services",
    //  icon: Smartphone,
    //  component: DigitalQuotes,
    //},
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      component: SettingsPage,
    },
  ];
};

  const navigationItems = getNavigationItems();

  // Generic handlers for CRUD operations
  const handleCreate = () => {
    setSelectedItem(null);
    setCurrentView("create");
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setCurrentView("edit");
  };

  const handleSave = (itemData) => {
    setCurrentView("list");
    setSelectedItem(null);
    setRefreshKey((prev) => prev + 1);
  };

  const handleCancel = () => {
    setCurrentView("list");
    setSelectedItem(null);
  };

  const handleDelete = (itemId) => {
    setRefreshKey((prev) => prev + 1);
  };
  
  const openNote = (connectionId) => {
    const searchParams = new URLSearchParams(window.location.search);
    const inviteToken = searchParams.get("invite") || "";

    // Update URL
    window.history.pushState({}, "", `/notes/${connectionId}${inviteToken ? `?invite=${inviteToken}` : ""}`);

    // ✅ Directly update route and state, no async delay
    handleRoute();
  };

  // Get current navigation item
  const currentNavItem = navigationItems.find(item => item.id === currentRoute);

  // Render page title based on current route and view
  const getPageTitle = () => {
    if (!currentNavItem) return "Dashboard";
    
    const baseTitle = currentNavItem.label;

    if (currentView === "create") {
      return `Create ${baseTitle}`;
    } else if (currentView === "edit") {
      return `Edit ${baseTitle}`;
    }

    return baseTitle;
  };

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    const breadcrumbs = ["Home"];

    if (currentRoute !== "dashboard") {
      breadcrumbs.push(currentNavItem?.label || currentRoute);
    }

    if (currentView !== "list") {
      breadcrumbs.push(currentView === "create" ? "Create" : "Edit");
    }

    return (
      <nav className="flex items-center text-sm text-gray-600 mb-6">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="mx-2 text-gray-400">/</span>
            )}
            <span
              className={
                index === breadcrumbs.length - 1
                  ? "text-blue-700 font-medium"
                  : "text-gray-500 hover:text-blue-600 transition-colors"
              }
            >
              {crumb}
            </span>
          </React.Fragment>
        ))}
      </nav>
    );
  };

  // Render main content based on current route and view
  const renderMainContent = () => {
    // Dashboard view
    if (currentRoute === "dashboard") {
        const isAdmin = currentUser?.role === "Admin";
        
        return (
          <div className="space-y-6">
            {/* Dashboard Header */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome, {currentUser?.name}!
              </h1>
              <p className="text-gray-600">
                {isAdmin 
                  ? "Admin Dashboard - Viewing all system statistics"
                  : `Your Dashboard - Viewing your personal statistics as ${currentUser?.role}`
                }
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isAdmin ? "Total Quotes" : "Your Total Quotes"}
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {stats.data.completed_rentals}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {isAdmin 
                    ? "Completed quotes in the system"
                    : "Your completed quotes"
                  }
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isAdmin ? "Pending Approvals" : "Your Pending Approvals"}
                </h3>
                <p className="text-3xl font-bold text-orange-600">
                  {stats.data.pending_approvals}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {isAdmin 
                    ? "All pending approvals in the system"
                    : "Your pending approvals"
                  }
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isAdmin ? "Quotes This Month" : "Your This Month"}
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {stats.data.completed_this_month}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {isAdmin 
                    ? "Quotes completed this month system-wide"
                    : "Your quotes completed this month"
                  }
                </p>
              </div>
            </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => updateURL(item.id)}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-100 transition-colors"
                  >
                    <div className="bg-blue-100 p-3 rounded-full mb-3">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Additional role-specific information */}
          {!isAdmin && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Personal Performance Summary
              </h3>
              <p className="text-blue-700">
                You have completed <span className="font-bold">{stats.data.completed_rentals}</span> quotes total, 
                with <span className="font-bold">{stats.data.completed_this_month}</span> completed this month.
              </p>
              <p className="text-blue-700 mt-2">
                You currently have <span className="font-bold">{stats.data.pending_approvals}</span> pending approvals.
              </p>
            </div>
          )}
        </div>
      );
    }

    // Form views (create/edit)
    if (currentView === "create" || currentView === "edit") {
      return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <UserForm
            user={selectedItem}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      );
    }
    
    if (currentRoute === "notes") {
      const searchParams = new URLSearchParams(window.location.search);
      const inviteToken = searchParams.get("invite");

      if (!selectedItem) {
        return (
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-gray-600 text-lg">Loading note...</p>
          </div>
        );
      }

      return <NotesComponent connectionId={selectedItem} inviteToken={inviteToken}    onNavigate={(path) => {
        window.history.pushState({}, "", path);
        handleRoute(); // call App's handleRoute
      }}/>;
    }
    
    if (currentRoute === "admin-config") {
      return (
        <AdminSystemConfig selectedConnectionId={selectedItem} />
      );
    }

    // List views
    const CurrentComponent = currentNavItem?.component;

    if (!CurrentComponent) {
      return (
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="max-w-md mx-auto">
            <div className="bg-blue-100 p-4 rounded-full inline-flex mb-4">
              <Settings className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {currentNavItem?.label || "Page"} Coming Soon
            </h3>
            <p className="text-gray-600 mb-6">
              This section is currently under development and will be available soon.
            </p>
            <button
              onClick={() => updateURL("dashboard")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    // Default component render
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <CurrentComponent
          key={refreshKey}
          selectedConnectionId={selectedItem}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreate={handleCreate}
          onViewNote={openNote} 
          currentUser={currentUser} 
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex">
      {/* Sidebar - Fixed and scrollable */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-800 to-indigo-900 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ overflowY: 'auto' }}
      >
        <div className="flex items-center justify-between h-20 px-6 border-b border-indigo-700">
          <div className="flex items-center space-x-3">
            <img 
              src="/images/FibreLogo.jpg" 
              alt="Company Logo" 
              className="h-12 w-auto rounded-lg bg-white p-1"
            />
            <h1 className="text-xl font-bold text-white">
              {currentUser?.role === "Admin" ? "Admin Panel" : "Sales Portal"}
            </h1>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden text-indigo-200 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentRoute === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  updateURL(item.id);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 text-left rounded-lg mx-2 mb-1 transition-colors ${
                  isActive
                    ? "bg-indigo-700 text-white shadow-md"
                    : "text-indigo-200 hover:bg-indigo-700 hover:text-white"
                }`}
              >
                <Icon
                  className={`h-5 w-5 mr-3 ${
                    isActive ? "text-white" : "text-indigo-300"
                  }`}
                />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main content - Offset for fixed sidebar */}
      <div className="flex-1 flex flex-col overflow-hidden ml-0 lg:ml-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 mr-4"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {getPageTitle()}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                  {currentUser?.name?.charAt(0) || "U"}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {currentUser?.name || "User"}
                  </p>
                  <p className="text-xs text-gray-500">{currentUser?.role}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto">
            {renderBreadcrumbs()}
            {renderMainContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;